import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@availity/element';
import CarouselAngleLeft from '../../../static/assets/icons/carousel-angle-left.svg';
import CarouselAngleRight from '../../../static/assets/icons/carousel-angle-right.svg';
import Animation from '../../Utils/components/Animation';

const ScalableCard = ({ title, description, visual }) => (
  <Box className="scalable-card">
    <Box className="scalable-card__visual">
      {visual}
    </Box>
    <Box className="scalable-card__content">
      <Typography className="scalable-card__title">
        {title}
      </Typography>
      <Typography className="scalable-card__description">
        {description}
      </Typography>
    </Box>
  </Box>
);

ScalableCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  visual: PropTypes.node.isRequired,
};

const AnimationVisual = ({ src }) => (
  <Animation src={src} sx={{ width: '100%', height: '100%' }} />
);

const cards = [
  {
    title: 'Future-Ready',
    description: 'Easily integrate new APIs as your needs evolve.',
    src: '/static/assets/illustrations/Animation-Bundled-Loop.html',
  },
  {
    title: 'Elastic Infrastructure',
    description: 'Handle millions of transactions without performance loss.',
    src: '/static/assets/illustrations/API-Marketplace-Elastic.html',
  },
  {
    title: 'High Availability',
    description: '99.9% uptime SLA for uninterrupted service.',
    src: '/static/assets/illustrations/API-Marketplace-Uptime.html',
  },
];

const ScalableSection = () => {
  const [currentCard, setCurrentCard] = React.useState(0);
  const [direction, setDirection] = React.useState<'left' | 'right'>('right');
  const [isAnimating, setIsAnimating] = React.useState(false);
  const touchStartX = React.useRef(0);

  const handleNext = () => {
    if (isAnimating) return;
    setDirection('right');
    setIsAnimating(true);
    setCurrentCard((prev) => (prev + 1) % cards.length);
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setDirection('left');
    setIsAnimating(true);
    setCurrentCard((prev) => (prev - 1 + cards.length) % cards.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      if (delta > 0) handleNext();
      else handlePrev();
    }
  };

  return (
  <Box
    sx={{
      width: 1,
      py: { xs: 8, md: 16.5 },
      background: '#000C30',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: { xs: 6, md: 11 },
    }}
  >
    <Box
      sx={{
        width: { xs: 1, lg: 976 },
        maxWidth: 976,
        px: { xs: 2, lg: 0 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography
        sx={{
          textAlign: 'center',
          color: '#91B0DA',
          fontSize: { xs: 18, md: 18 },
          fontFamily: 'Lato',
          fontWeight: 400,
          lineHeight: 26/18,
        }}
      >
        Scalable by Design
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          color: 'white',
          fontSize: { xs: 28, md: 36 },
          fontFamily: 'Montserrat',
          fontWeight: 600,
          lineHeight: 48/36,
        }}
      >
        Built to Scale with Your Business
      </Typography>
    </Box>

    {/* Desktop view - all cards */}
    <Box
      sx={{
        width: { xs: 1, lg: 1200 },
        maxWidth: 1200,
        px: { xs: 2, lg: 0 },
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 3,
      }}
    >
      {cards.map((card) => (
        <ScalableCard
          key={card.title}
          title={card.title}
          description={card.description}
          visual={<AnimationVisual src={card.src} />}
        />
      ))}
    </Box>

    {/* Mobile/Tablet view - carousel */}
    <Box
      sx={{
        width: { xs: 1, lg: 1200 },
        maxWidth: 1200,
        px: { xs: 2, lg: 0 },
        display: { xs: 'flex', lg: 'none' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <Box
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          width: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {/* Left Arrow */}
        <Box
          onClick={handlePrev}
          sx={{
            cursor: 'pointer',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src={CarouselAngleLeft} alt="Previous" width="42" height="42" />
        </Box>

        {/* Active card with slide transition */}
        <Box sx={{ flex: 1, maxWidth: 380, minWidth: 0, overflow: 'hidden' }}>
          <Box
            key={currentCard}
            onAnimationEnd={() => setIsAnimating(false)}
            sx={{
              '@keyframes slideInRight': { from: { transform: 'translateX(100%)', opacity: 0 }, to: { transform: 'translateX(0)', opacity: 1 } },
              '@keyframes slideInLeft': { from: { transform: 'translateX(-100%)', opacity: 0 }, to: { transform: 'translateX(0)', opacity: 1 } },
              animation: `${direction === 'right' ? 'slideInRight' : 'slideInLeft'} 0.3s ease-out`,
            }}
          >
            <ScalableCard
              title={cards[currentCard].title}
              description={cards[currentCard].description}
              visual={<AnimationVisual src={cards[currentCard].src} />}
            />
          </Box>
        </Box>

        {/* Right Arrow */}
        <Box
          onClick={handleNext}
          sx={{
            cursor: 'pointer',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img src={CarouselAngleRight} alt="Next" width="42" height="42" />
        </Box>
      </Box>
    </Box>
  </Box>
  );
};

export default ScalableSection;